/*!
 * scrolling-text.js
 * Standalone JS for the "Scrolling text" Shopify section.
 *
 * Provides two custom elements:
 *   <marquee-element> — clones its first child enough times to fill the row
 *                       and applies a CSS animation by toggling `.animated`.
 *   <em is="highlighted-text"> — adds `.animated` when scrolled into view so
 *                                the CSS highlight transition can play.
 *
 * No dependencies. Works with or without the rest of the theme's JS bundles.
 * Safe to re-import: both defines are guarded against double-registration.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var isTouch = ('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0) ||
    (navigator.msMaxTouchPoints > 0);

  var isRTL = document.documentElement.getAttribute('dir') === 'rtl';

  /* ------------------------------------------------------------------------
   * <em is="highlighted-text">
   * Adds `.animated` when first scrolled into view.
   * ------------------------------------------------------------------------ */
  if (!customElements.get('highlighted-text')) {
    var HighlightedText = /** @class */ (function () {
      function HighlightedTextClass() {
        var el = Reflect.construct(HTMLElement, [], HighlightedTextClass);
        return el;
      }
      HighlightedTextClass.prototype = Object.create(HTMLElement.prototype);
      HighlightedTextClass.prototype.constructor = HighlightedTextClass;

      HighlightedTextClass.prototype.connectedCallback = function () {
        if (this._observed) return;
        this._observed = true;
        var self = this;
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              self.classList.add('animated');
              io.disconnect();
            }
          });
        }, { rootMargin: '0px 0px -10% 0px' });
        io.observe(this);
      };

      Object.setPrototypeOf(HighlightedTextClass, HTMLElement);
      return HighlightedTextClass;
    })();

    try {
      customElements.define('highlighted-text', HighlightedText, { extends: 'em' });
    } catch (e) {
      // Safari lacks customized built-in support — degrade silently.
      // CSS still applies via the .highlighted-text class; animation will be
      // handled by the reduced-motion / always-on fallbacks in the CSS.
    }
  }

  /* ------------------------------------------------------------------------
   * <marquee-element>
   * ------------------------------------------------------------------------ */
  if (!customElements.get('marquee-element')) {
    class MarqueeElement extends HTMLElement {
      constructor() {
        super();
        this._initialized = false;
      }

      connectedCallback() {
        if (prefersReducedMotion) return;
        if (this._observed) return;
        this._observed = true;

        var self = this;
        var startObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              self._init();
              startObserver.disconnect();
            }
          });
        }, { rootMargin: '200px 0px 200px 0px' });
        startObserver.observe(this);
      }

      get childElement() {
        return this.firstElementChild;
      }

      get speed() {
        return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed'), 10) : 16;
      }

      get maximum() {
        if (this.hasAttribute('data-maximum')) {
          return parseInt(this.getAttribute('data-maximum'), 10);
        }
        return Math.ceil(this._parentWidth / this._childWidth) + 2;
      }

      get direction() {
        return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
      }

      get parallax() {
        return !isTouch && this.hasAttribute('data-parallax')
          ? parseFloat(this.getAttribute('data-parallax'))
          : false;
      }

      get _parentWidth() { return this._widthOf(this); }
      get _childWidth() { return this._widthOf(this.childElement); }

      _widthOf(el) {
        if (!el) return 0;
        var rect = el.getBoundingClientRect();
        return rect.right - rect.left;
      }

      _init() {
        if (this._initialized || !this.childElement) return;
        this._initialized = true;

        if (this.childElementCount === 1) {
          this.childElement.classList.add('animated');

          var max = this.maximum;
          for (var i = 0; i < max; i++) {
            var clone = this.childElement.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            this.appendChild(clone);
            clone.querySelectorAll('.media').forEach(function (m) {
              m.classList.remove('loading');
            });
          }

          var duration = (33 - this.speed) *
            Math.min(2.5, Math.ceil(this._childWidth / this._parentWidth));
          this.style.setProperty('--duration', duration + 's');
        }

        if (this.parallax) {
          this._setupParallax();
        } else {
          this._setupPauseWhenOffscreen();
        }
      }

      _setupPauseWhenOffscreen() {
        var self = this;
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              self.classList.remove('paused');
            } else {
              self.classList.add('paused');
            }
          });
        }, { rootMargin: '0px 0px 50px 0px' });
        observer.observe(this);
      }

      _setupParallax() {
        var factor = this.parallax;
        var base = (factor * 100) / (1 + factor);
        if (this.direction === 'right') base = -base;
        if (isRTL) base = -base;

        var self = this;
        var ticking = false;

        function update() {
          ticking = false;
          var rect = self.getBoundingClientRect();
          var vh = window.innerHeight || document.documentElement.clientHeight;
          // progress from 0 (entering viewport bottom) to 1 (leaving viewport top)
          var total = vh + rect.height;
          var progress = Math.min(1, Math.max(0, (vh - rect.top) / total));
          var translate = base * (1 - progress);
          self.style.transform = 'translateX(' + translate.toFixed(2) + '%)';
        }

        function onScroll() {
          if (!ticking) {
            window.requestAnimationFrame(update);
            ticking = true;
          }
        }

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        update();
      }
    }

    customElements.define('marquee-element', MarqueeElement);
  }
})();